import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { type AccessProfile, type AccessProfileVersion, type ApiError, type ProfileAuditEntry, type ProfileCatalog } from '@vello/api-client';
import {
  PROFILE_REASON_CODES, authorityWordsOf, canDecideVersion, maySelfApprove, canRetire, canWithdrawVersion, diffWords, emptyProfileForm, formFromProfile, formNeedsFourEyes,
  normalizeProfileForm, setBaseRole, toggleGroup, toggleWord, wordGroups,
  type ProfileForm, type ProfileFormIssue, type ProfileReasonCode,
} from '../../src/index';
import { useConsole } from '../console';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, CheckRow, EmptyResult, Field, Notice, PageHeader, Select, TextArea, TextInput, Text, Row, Box, Line, RecordSection } from '@vello/web-theme';


type Msg = { tone: 'success' | 'danger' | 'warning'; text: string };
/** `create` a profile, `change` the words of one (a new version), `copy` one under a new name. */
type Editor = { mode: 'create' } | { mode: 'change'; profile: AccessProfile } | { mode: 'copy'; profile: AccessProfile };

/**
 * Configurações › **Perfis de acesso** (W9-PROFILES-REGISTRIES-KEYS, D475, ADR-0093; DevMobility A-018/A-019). A profile is a
 * SUBSET of a base role's words with a home screen — never a free composition: the checklist offers only the words the role
 * grants. Create, change (a new version), copy and retire. A profile whose words reach admin authority (it can manage operators,
 * profiles, markets or the governed settings) waits for a DIFFERENT operator, who approves it here; the words in force stay the
 * previous version's until then. The operators screen grants a profile to an operator.
 */
export function AccessProfilesScreen(): ReactElement {
  const { runtime, grants } = useAdmin();
  const selfApproval = maySelfApprove(grants); // D868: a GLOBAL super administrator decides their own proposals
  const { confirm } = useConsole();
  const { t } = useT();
  const { fail, v, reason: reasonText } = useVocab();
  const api = runtime.accessProfiles;

  const [profiles, setProfiles] = useState<readonly AccessProfile[] | undefined>(undefined);
  const [catalog, setCatalog] = useState<ProfileCatalog | undefined>(undefined);
  const [msg, setMsg] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState<Editor | undefined>(undefined);
  const [form, setForm] = useState<ProfileForm>(() => emptyProfileForm());
  const [reason, setReason] = useState<ProfileReasonCode>('profile_new_function');
  const [issues, setIssues] = useState<readonly ProfileFormIssue[]>([]);
  const [history, setHistory] = useState<{ id: string; versions: readonly AccessProfileVersion[]; audit: readonly ProfileAuditEntry[] } | undefined>(undefined);

  const refusal = useCallback((e: ApiError): string => (e.code === 'FORBIDDEN' ? t('pfl.denied') : t('pfl.failed', { code: fail(e) })), [t]);

  const load = useCallback(async (): Promise<void> => {
    const [list, cat] = await Promise.all([api.list(), api.catalog()]);
    if (list.ok) setProfiles(list.value.profiles);
    else {
      setProfiles([]);
      setMsg({ tone: 'danger', text: refusal(list.error) });
    }
    if (cat.ok) setCatalog(cat.value);
  }, [api, refusal]);

  useEffect(() => {
    void load();
  }, [load]);

  const groups = useMemo(() => (form.baseRole === '' ? [] : wordGroups(form.baseRole)), [form.baseRole]);
  const authority = useMemo(() => new Set(authorityWordsOf(form.words)), [form.words]);
  const issueText = (i: ProfileFormIssue): string | undefined => (issues.includes(i) ? reasonText(i) : undefined);

  const open = (next: Editor): void => {
    setMsg(undefined);
    setIssues([]);
    setEditor(next);
    if (next.mode === 'create') {
      setForm(emptyProfileForm());
      setReason('profile_new_function');
    } else if (next.mode === 'change') {
      setForm(formFromProfile(next.profile));
      setReason('profile_correction');
    } else {
      setForm({ ...formFromProfile(next.profile), name: '' });
      setReason('profile_copy');
    }
  };

  const onSave = async (): Promise<void> => {
    if (editor === undefined) return;
    setMsg(undefined);
    const normalized = normalizeProfileForm(form, reason);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res =
      editor.mode === 'create' ? await api.create(normalized.value)
      : editor.mode === 'copy' ? await api.copy(editor.profile.id, normalized.value.name, normalized.value.note)
      : await api.propose(editor.profile.id, { permissions: normalized.value.permissions, ...(normalized.value.homeScreen !== undefined ? { homeScreen: normalized.value.homeScreen } : {}), reasonCode: normalized.value.reasonCode, ...(normalized.value.note !== undefined ? { note: normalized.value.note } : {}) });
    setBusy(false);
    if (!res.ok) {
      setMsg({ tone: 'danger', text: refusal(res.error) });
      return;
    }
    setMsg(res.value.fourEyes ? { tone: 'warning', text: t('pfl.saved.fourEyes', { name: normalized.value.name }) } : { tone: 'success', text: t('pfl.saved', { name: normalized.value.name }) });
    setEditor(undefined);
    setHistory(undefined);
    await load();
  };

  const onDecide = async (p: AccessProfile, v: AccessProfileVersion, action: 'approve' | 'reject' | 'withdraw'): Promise<void> => {
    let note: string | undefined;
    if (action === 'reject') {
      const answer = await confirm({ title: t('pfl.reject'), body: t('pfl.rejectNote'), confirmLabel: t('pfl.reject'), cancelLabel: t('pfl.cancel'), tone: 'danger', notePlaceholder: t('pfl.rejectNote') });
      if (!answer.confirmed || answer.note === undefined || answer.note.trim() === '') return;
      note = answer.note;
    }
    const res = await api.decide(p.id, v.version, action, note);
    setMsg(res.ok ? { tone: 'success', text: t('pfl.done') } : { tone: 'danger', text: refusal(res.error) });
    if (res.ok) {
      setHistory(undefined);
      await load();
    }
  };

  const onRetire = async (p: AccessProfile): Promise<void> => {
    const labels = PROFILE_REASON_CODES.map((c) => v('rbac.profile_reason', c));
    const answer = await confirm({
      title: t('pfl.retire.title', { name: p.name }), body: t('pfl.retire.body'), confirmLabel: t('pfl.retire'), cancelLabel: t('pfl.cancel'), tone: 'danger', icon: 'shieldAlert',
      reasons: labels, reasonLabel: t('pfl.field.reason'),
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const code = PROFILE_REASON_CODES[labels.indexOf(answer.reason)];
    if (code === undefined) return;
    const res = await api.retire(p.id, code);
    setMsg(res.ok ? { tone: 'success', text: t('pfl.retired', { name: p.name }) } : { tone: 'danger', text: refusal(res.error) });
    if (res.ok) await load();
  };

  const onHistory = async (p: AccessProfile): Promise<void> => {
    if (history?.id === p.id) {
      setHistory(undefined);
      return;
    }
    const res = await api.get(p.id);
    if (res.ok) setHistory({ id: p.id, versions: res.value.versions, audit: res.value.audit });
    else setMsg({ tone: 'danger', text: refusal(res.error) });
  };

  const pendingQueue = (profiles ?? []).filter((p) => p.pending !== undefined && canDecideVersion(p.pending, selfApproval));

  return (
    <div>
      <PageHeader title={t('pfl.title')} subtitle={t('pfl.subtitle')} actions={<Button variant="secondary" onClick={() => open({ mode: 'create' })} data-testid="pfl-new">{t('pfl.new')}</Button>} />
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {pendingQueue.length > 0 && (
        <Box as="section" gap={3} pad={4} aria-label={t('pfl.queue.title')} data-testid="pfl-queue">
          <strong>{t('pfl.queue.title')}</strong>
          {pendingQueue.map((p) => {
            const ver = p.pending as AccessProfileVersion;
            const diff = diffWords(p.live?.permissions ?? [], ver.permissions);
            return (
              <Line key={p.id} wrap pad={3} ruled gap={2} data-testid={`pfl-pending-${p.id}`}>
                <div>
                  <span>{t('pfl.queue.row', { name: p.name, role: v('rbac.role', p.baseRole), version: ver.version })}</span>
                  <Text role="caption" tone="secondary" block>{t('pfl.diff', { added: diff.added.length, removed: diff.removed.length })}{diff.added.length > 0 ? ` · + ${diff.added.map((w) => v('rbac.permission', w)).join(', ')}` : ''}</Text>
                  <Text role="caption" tone="secondary" block>{v('rbac.profile_reason', ver.reasonCode)}{ver.note !== undefined ? ` · ${ver.note}` : ''}</Text>
                </div>
                <Row gap={2} align="center" wrap>
                  <Button variant="secondary" size="sm" onClick={() => void onDecide(p, ver, 'approve')} data-testid={`pfl-approve-${p.id}`}>{t('pfl.approve')}</Button>
                  {ver.mine !== true && <Button variant="ghost" size="sm" onClick={() => void onDecide(p, ver, 'reject')}>{t('pfl.reject')}</Button>}
                </Row>
              </Line>
            );
          })}
        </Box>
      )}

      {editor !== undefined && catalog !== undefined && (
        <Box as="section" gap={3} pad={4} aria-label={t(`pfl.editor.${editor.mode}`)} data-testid="pfl-editor">
          <strong>{t(`pfl.editor.${editor.mode}`)}{editor.mode !== 'create' ? ` · ${editor.profile.name}` : ''}</strong>
          {editor.mode !== 'change' && (
            <Field label={t('pfl.field.name')} htmlFor="pfl-name" hint={issueText('NAME_INVALID')}>
              <TextInput id="pfl-name" value={form.name} maxLength={catalog.nameMax} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
            </Field>
          )}
          <Field label={t('pfl.field.baseRole')} htmlFor="pfl-role" hint={issueText('BASE_ROLE_INVALID') ?? t('pfl.field.baseRole.hint')}>
            <Select id="pfl-role" value={form.baseRole} disabled={editor.mode !== 'create'} onChange={(e) => setForm((f) => setBaseRole(f, e.target.value))}>
              <option value="">—</option>
              {catalog.roles.map((r) => <option key={r.role} value={r.role}>{v('rbac.role', r.role)}</option>)}
            </Select>
          </Field>
          <Field label={t('pfl.field.homeScreen')} htmlFor="pfl-home" hint={issueText('HOME_SCREEN_INVALID') ?? t('pfl.field.homeScreen.hint')}>
            <Select id="pfl-home" value={form.homeScreen} onChange={(e) => setForm((f) => ({ ...f, homeScreen: e.target.value }))}>
              <option value="">{t('pfl.homeScreen.default')}</option>
              {catalog.homeScreens.map((s) => <option key={s} value={s}>{v('rbac.profile_home_screen', s)}</option>)}
            </Select>
          </Field>
          <div role="group" aria-label={t('pfl.field.words')} data-testid="pfl-words">
            <strong>{t('pfl.field.words')}</strong>
            <Text role="caption" tone="secondary" block>{t('pfl.field.words.hint', { n: form.words.length })}</Text>
            {issueText('WORDS_REQUIRED') !== undefined && <Text role="caption" tone="danger" block>{issueText('WORDS_REQUIRED')}</Text>}
            {issueText('WORD_NOT_IN_BASE_ROLE') !== undefined && <Text role="caption" tone="danger" block>{issueText('WORD_NOT_IN_BASE_ROLE')}</Text>}
            {form.baseRole === '' && <Text role="caption" tone="secondary" block>{t('pfl.words.pickRole')}</Text>}
            {groups.map((g) => {
              const all = g.words.every((w) => form.words.includes(w));
              return (
                <RecordSection key={g.resource}>
                  <CheckRow id={`pfl-group-${g.resource}`} checked={all} onChange={() => setForm((f) => toggleGroup(f, g.words))}><strong>{v('rbac.resource', g.resource)}</strong></CheckRow>
                  <Row gap={3} align="stretch" wrap>
                    {g.words.map((w) => (
                      <CheckRow key={w} id={`pfl-word-${w}`} checked={form.words.includes(w)} onChange={() => setForm((f) => toggleWord(f, w))}>
                        <span>{v('rbac.permission', w)}</span>
                        {catalog.roles.find((r) => r.role === form.baseRole)?.words.find((x) => x.word === w)?.adminAuthority === true && <Badge tone="warning">{t('pfl.authority')}</Badge>}
                      </CheckRow>
                    ))}
                  </Row>
                </RecordSection>
              );
            })}
          </div>
          {formNeedsFourEyes(form) && <Notice tone="warning">{t('pfl.fourEyesHint', { words: [...authority].map((w) => v('rbac.permission', w)).join(', ') })}</Notice>}
          <Field label={t('pfl.field.reason')} htmlFor="pfl-reason">
            <Select id="pfl-reason" value={reason} onChange={(e) => setReason(e.target.value as ProfileReasonCode)}>
              {PROFILE_REASON_CODES.map((c) => <option key={c} value={c}>{v('rbac.profile_reason', c)}</option>)}
            </Select>
          </Field>
          <Field label={t('pfl.field.note')} htmlFor="pfl-note"><TextArea id="pfl-note" rows={2} maxLength={500} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} /></Field>
          <Row gap={2} align="center" wrap>
            <Button variant="primary" disabled={busy} onClick={() => void onSave()} data-testid="pfl-save">{t(formNeedsFourEyes(form) ? 'pfl.save.fourEyes' : 'pfl.save')}</Button>
            <Button variant="ghost" onClick={() => setEditor(undefined)}>{t('pfl.cancel')}</Button>
          </Row>
        </Box>
      )}

      {profiles !== undefined && profiles.length === 0 && msg?.tone !== 'danger' && <EmptyResult title={t('pfl.empty.title')} detail={t('pfl.empty.detail')} />}
      {profiles !== undefined && profiles.length > 0 && (
        <div role="list">
          {profiles.map((p) => (
            <div key={p.id} role="listitem" data-testid={`pfl-row-${p.id}`}>
              <Line wrap pad={3} ruled gap={2}>
                <div>
                  <strong>{p.name}</strong>
                  <Text role="caption" tone="secondary" block>{v('rbac.role', p.baseRole)} · {t('pfl.row.words', { n: p.live?.permissions.length ?? 0 })} · {t('pfl.row.grants', { n: p.liveGrants })}</Text>
                  <Text role="caption" tone="secondary" block>{p.live?.homeScreen !== undefined ? t('pfl.row.home', { screen: v('rbac.profile_home_screen', p.live.homeScreen) }) : t('pfl.homeScreen.default')}</Text>
                </div>
                <Row gap={2} align="center" wrap>
                  {p.live === undefined && <Badge tone="warning">{t('pfl.badge.notApproved')}</Badge>}
                  {p.pending !== undefined && <Badge tone="warning">{t('pfl.badge.pending', { version: p.pending.version })}</Badge>}
                  {p.live?.fourEyes === true && <Badge tone="neutral">{t('pfl.authority')}</Badge>}
                  {p.live !== undefined && <Button variant="secondary" size="sm" disabled={p.pending !== undefined} onClick={() => open({ mode: 'change', profile: p })} data-testid={`pfl-change-${p.id}`}>{t('pfl.change')}</Button>}
                  {p.live !== undefined && <Button variant="ghost" size="sm" onClick={() => open({ mode: 'copy', profile: p })} data-testid={`pfl-copy-${p.id}`}>{t('pfl.copy')}</Button>}
                  <Button variant="ghost" size="sm" onClick={() => void onHistory(p)} data-testid={`pfl-history-${p.id}`}>{t(history?.id === p.id ? 'pfl.history.close' : 'pfl.history.open')}</Button>
                  {p.pending !== undefined && canWithdrawVersion(p.pending) && <Button variant="ghost" size="sm" onClick={() => void onDecide(p, p.pending as AccessProfileVersion, 'withdraw')}>{t('pfl.withdraw')}</Button>}
                  {canRetire(p) && <Button variant="danger" size="sm" onClick={() => void onRetire(p)} data-testid={`pfl-retire-${p.id}`}>{t('pfl.retire')}</Button>}
                </Row>
              </Line>
              {history?.id === p.id && (
                <Box as="section" gap={3} pad={4} aria-label={t('pfl.history.title')} data-testid={`pfl-versions-${p.id}`}>
                  <strong>{t('pfl.history.title')}</strong>
                  {history.versions.map((ver, i) => {
                    const previous = history.versions.find((x) => x.version < ver.version && x.state === 'APPROVED');
                    const diff = diffWords(previous?.permissions ?? [], ver.permissions);
                    return (
                      <Line key={ver.version} wrap pad={3} ruled gap={2}>
                        <span>
                          <span className="tnum">v{ver.version}</span> · {v('operator.access_profile_version_state', ver.state)} · {v('rbac.profile_reason', ver.reasonCode)}
                          <Text role="caption" tone="secondary" block>{t('pfl.diff', { added: diff.added.length, removed: diff.removed.length })}{i === 0 && ver.homeScreen !== undefined ? ` · ${v('rbac.profile_home_screen', ver.homeScreen)}` : ''}</Text>
                        </span>
                        <Text role="caption" tone="secondary" block>{ver.proposedAt.slice(0, 16).replace('T', ' ')}</Text>
                      </Line>
                    );
                  })}
                  {history.audit.length > 0 && <Text role="caption" tone="secondary" block>{t('pfl.history.audit', { n: history.audit.length })}</Text>}
                </Box>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
